import { unparse } from 'papaparse';
import * as XLSX from 'xlsx';

export type ExportFormat = 'csv' | 'json' | 'xlsx';

/**
 * Exports data to different file formats
 * @param data Array of objects to export
 * @param format Export format (csv, json, xlsx)
 * @param fileName Base file name without extension
 * @param sheetName Sheet name for Excel exports
 */
export const exportData = <T extends Record<string, any>>(
  data: T[],
  format: ExportFormat = 'csv',
  fileName: string = `export-${new Date().toISOString().split('T')[0]}`,
  sheetName: string = 'Data'
): void => {
  if (!data || data.length === 0) return;

  if (format === 'csv') {
    // Use PapaParse for CSV export
    const csv = unparse(data, {
      quotes: true, // Use quotes around all fields
      quoteChar: '"',
      escapeChar: '"',
      delimiter: ',',
      header: true,
      newline: '\n',
    });

    // Create a blob and download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    downloadFile(blob, `${fileName}.csv`);
  } else if (format === 'json') {
    // Format JSON with indentation for readability
    const jsonContent = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json' });
    downloadFile(blob, `${fileName}.json`);
  } else if (format === 'xlsx') {
    // Use SheetJS for Excel export
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    // Generate Excel file as an array buffer
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

    // Create a blob and download
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    downloadFile(blob, `${fileName}.xlsx`);
  }
};

/**
 * Helper function to download a file
 * @param blob Blob to download
 * @param fileName File name with extension
 */
const downloadFile = (blob: Blob, fileName: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', fileName);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url); // Clean up
};
